// Product form pieces for the optional field groups: brand, attribute fields
// (pharmacy, clothing, custom…) and pack sizes / extra barcodes.
import {
  defaultOtherPrice,
  shelfLifeLabel,
  stockWords,
  unitName,
  unitWords,
  type TwoUnits,
  type UnitChoice,
} from '@ims/core'
import type { JsonValue, ProductCodeRow, ProductRow, RowOf } from '@ims/data'
import {
  Banner,
  Button,
  Card,
  Checkbox,
  Field,
  IconButton,
  Input,
  Select,
  Switch,
  cx,
} from '@ims/ui'
import { Link2, LockKeyhole, Plus, Trash2 } from '@ims/ui/icons'
import { useState } from 'react'
import { MoneyInput, QtyInput, type Lookups } from '../components.tsx'
import {
  FIELD_GROUPS,
  formatAttribute,
  type AttributeField,
  type FieldGroup,
} from '../lib/catalog.ts'
import { money, qty } from '../lib/format.ts'
import {
  findUnit,
  pluralUnit,
  presetFor,
  unitForNew,
  type QuickPair,
  type TwoUnitsChecks,
} from '../lib/units.ts'

type UnitRow = RowOf<'units'>

export function SectionTitle({ children }: { children: string }) {
  return (
    <h3 className="-mb-1 mt-2 flex items-center gap-3 text-section text-muted after:h-px after:flex-1 after:bg-border sm:col-span-2">
      {children}
    </h3>
  )
}

export function BrandField({
  lookups,
  brandId,
  onBrand,
  newBrandName,
  onNewBrandName,
  placeholder,
}: {
  lookups: Lookups
  brandId: string
  onBrand: (id: string) => void
  newBrandName: string
  onNewBrandName: (name: string) => void
  /** An example brand of the business's kind. */
  placeholder?: string
}) {
  return (
    <>
      <Field label="Brand">
        {(id) => (
          <Select id={id} value={brandId} onChange={(e) => onBrand(e.target.value)}>
            <option value="">—</option>
            {lookups.brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
            <option value="__new">+ New brand…</option>
          </Select>
        )}
      </Field>
      {brandId === '__new' && (
        <Field label="New brand name">
          {(id) => (
            <Input
              id={id}
              value={newBrandName}
              placeholder={placeholder}
              onChange={(e) => onNewBrandName(e.target.value)}
            />
          )}
        </Field>
      )}
    </>
  )
}

export function AttributeFields({
  sections,
  values,
  onChange,
}: {
  sections: { group: FieldGroup; fields: AttributeField[] }[]
  values: Record<string, JsonValue | undefined>
  onChange: (key: string, value: JsonValue | undefined) => void
}) {
  return (
    <>
      {sections.map(({ group, fields }) => (
        <div key={group.id} className="contents">
          <SectionTitle>{group.label}</SectionTitle>
          {fields.map((f) => (
            <AttributeInput
              key={f.key}
              field={f}
              value={values[f.key]}
              onChange={(v) => onChange(f.key, v)}
            />
          ))}
        </div>
      ))}
    </>
  )
}

function AttributeInput({
  field,
  value,
  onChange,
}: {
  field: AttributeField
  value: JsonValue | undefined
  onChange: (value: JsonValue | undefined) => void
}) {
  if (field.type === 'yesno') {
    return (
      <div className="flex items-end pb-2">
        <Checkbox
          label={field.label}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
      </div>
    )
  }
  return (
    <Field label={field.label}>
      {(id) =>
        field.type === 'select' ? (
          <Select
            id={id}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value || undefined)}
          >
            <option value="">—</option>
            {(field.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            id={id}
            type={field.type === 'date' ? 'date' : 'text'}
            inputMode={field.type === 'number' ? 'decimal' : undefined}
            placeholder={field.placeholder}
            value={value == null ? '' : String(value)}
            onChange={(e) => {
              const text = e.target.value
              if (field.type === 'number') {
                const n = Number(text)
                onChange(text.trim() === '' ? undefined : Number.isFinite(n) ? n : text)
              } else onChange(text)
            }}
          />
        )
      }
    </Field>
  )
}

/** A pack size or extra barcode being edited (id set when it already exists). */
export interface PackDraft {
  key: string
  id: string | null
  code: string
  label: string
  qtyMilli: number | null
  pricePaisa: number | null
  removed: boolean
}

export function packDrafts(codes: ProductCodeRow[]): PackDraft[] {
  return codes.map((c) => ({
    key: c.id,
    id: c.id,
    code: c.code,
    label: c.label ?? '',
    qtyMilli: c.qty_milli,
    pricePaisa: c.sell_price_paisa,
    removed: false,
  }))
}

export function PacksEditor({
  packs,
  onChange,
  allowsDecimal,
  unitSymbol,
}: {
  packs: PackDraft[]
  onChange: (packs: PackDraft[]) => void
  allowsDecimal: boolean
  unitSymbol: string
}) {
  const update = (key: string, change: Partial<PackDraft>) =>
    onChange(packs.map((p) => (p.key === key ? { ...p, ...change } : p)))
  const visible = packs.filter((p) => !p.removed)
  return (
    <div className="space-y-2 sm:col-span-2">
      <p className="text-xs text-muted">
        Scanning a pack barcode adds its quantity. Leave the pack price empty to charge the unit
        price × quantity. Use quantity 1 for an extra barcode of the same item.
      </p>
      {visible.map((p) => (
        <div
          key={p.key}
          className="grid grid-cols-2 items-end gap-2 rounded-xl border border-border p-2 sm:grid-cols-[1.4fr_1fr_0.8fr_1fr_auto]"
        >
          <Field label="Barcode">
            {(id) => (
              <Input
                id={id}
                value={p.code}
                onChange={(e) => update(p.key, { code: e.target.value })}
              />
            )}
          </Field>
          <Field label="Pack name">
            {(id) => (
              <Input
                id={id}
                value={p.label}
                placeholder="Box of 12"
                onChange={(e) => update(p.key, { label: e.target.value })}
              />
            )}
          </Field>
          <Field label={`Qty${unitSymbol ? ` (${unitSymbol})` : ''}`}>
            {(id) => (
              <QtyInput
                id={id}
                milli={p.qtyMilli}
                allowsDecimal={allowsDecimal}
                onValue={(m) => update(p.key, { qtyMilli: m })}
              />
            )}
          </Field>
          <Field label="Pack price">
            {(id) => (
              <MoneyInput
                id={id}
                paisa={p.pricePaisa}
                placeholder="optional"
                onValue={(v) => update(p.key, { pricePaisa: v })}
              />
            )}
          </Field>
          <IconButton
            label={`Remove ${p.label || p.code || 'pack'}`}
            onClick={() =>
              p.id
                ? update(p.key, { removed: true })
                : onChange(packs.filter((x) => x.key !== p.key))
            }
            className="mb-1"
          >
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      ))}
      <Button
        size="sm"
        variant="secondary"
        icon={<Plus className="size-3.5" />}
        onClick={() =>
          onChange([
            ...packs,
            {
              key: crypto.randomUUID(),
              id: null,
              code: '',
              label: '',
              qtyMilli: null,
              pricePaisa: null,
              removed: false,
            },
          ])
        }
      >
        Add pack or barcode
      </Button>
    </div>
  )
}

/** Everything extra about a product that has a value (whether or not its group is shown now). */
export function ProductDetailsCard({
  product,
  lookups,
  codes,
  custom,
  unitSymbol,
  settings,
  side,
  showPrice,
}: {
  product: ProductRow
  lookups: Lookups
  codes: ProductCodeRow[]
  custom: AttributeField[]
  unitSymbol: string
  settings: Parameters<typeof money>[1]
  /** In the page's side column: one label and value per line. */
  side?: boolean
  /** The selling price too (the stock view has no price card). */
  showPrice?: boolean
}) {
  const attrs =
    product.attributes &&
    typeof product.attributes === 'object' &&
    !Array.isArray(product.attributes)
      ? product.attributes
      : {}
  const known = [...FIELD_GROUPS.flatMap((g) => g.attributes ?? []), ...custom]
  const rows: [string, string][] = []
  const add = (label: string, value: string | null | undefined) => {
    if (value) rows.push([label, value])
  }
  add('Category', lookups.category(product.category_id)?.name)
  if (side) {
    add('Unit', lookups.unit(product.unit_id)?.name)
    add('VAT', lookups.taxRate(product.tax_rate_id)?.name ?? 'None')
  }
  if (showPrice && product.for_sale) add('Selling price', money(product.sell_price_paisa, settings))
  add('Brand', lookups.brand(product.brand_id)?.name)
  add('Manufacturer', product.manufacturer)
  add('MRP', product.mrp_paisa !== null ? money(product.mrp_paisa, settings) : null)
  add(
    'Wholesale price',
    product.wholesale_price_paisa !== null ? money(product.wholesale_price_paisa, settings) : null,
  )
  add(
    'Minimum price',
    product.min_price_paisa !== null ? money(product.min_price_paisa, settings) : null,
  )
  add('Scale item code (PLU)', product.plu_code)
  add('HS code', product.hs_code)
  add('Shelf / rack', product.shelf_location)
  add(
    'Shelf life',
    product.track_expiry && product.shelf_life_days
      ? shelfLifeLabel(product.shelf_life_days, product.shelf_life_unit)
      : null,
  )
  add('Tags', product.tags)
  add(
    'Reorder quantity',
    product.reorder_qty_milli ? `${qty(product.reorder_qty_milli)} ${unitSymbol}`.trim() : null,
  )
  for (const [key, value] of Object.entries(attrs)) {
    const field = known.find((f) => f.key === key)
    add(field?.label ?? key, formatAttribute(field ?? { key, label: key, type: 'text' }, value))
  }
  if (rows.length === 0 && codes.length === 0) return null
  return (
    <Card className={side ? undefined : 'mt-4'}>
      <h2 className="mb-3 font-semibold">Details</h2>
      {rows.length > 0 &&
        (side ? (
          <dl className="divide-y divide-border text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3 py-1.5">
                <dt className="text-muted">{label}</dt>
                <dd className="text-right font-semibold">{value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-muted">{label}</dt>
                <dd className="font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        ))}
      {codes.length > 0 && (
        <div className="mt-4">
          <p className="mb-1 text-xs text-muted">Pack sizes & extra barcodes</p>
          <ul className="divide-y divide-border rounded-xl border border-border text-sm">
            {codes.map((c) => (
              <li key={c.id} className="flex flex-wrap justify-between gap-2 px-3 py-2">
                <span>
                  <span className="font-medium">{c.label || 'Extra barcode'}</span>
                  <span className="ml-2 font-mono text-xs text-muted">{c.code}</span>
                </span>
                <span className="tabular text-muted">
                  {qty(c.qty_milli)} {unitSymbol}
                  {c.sell_price_paisa !== null ? ` · ${money(c.sell_price_paisa, settings)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}

export type TwoUnitsDraft = {
  on: boolean
  /** Unit names: a shop unit's name, or a new one made on save (`unitForNew`). */
  larger: string
  smaller: string
  count: number | null
  /** The other unit's own price; null follows the stock unit's price. */
  otherPricePaisa: number | null
  /** Which unit stock is counted in, as chosen in the form (null: not chosen yet). */
  pick: UnitChoice | null
  /** A new product's opening stock, typed in both units. */
  openLarger: number | null
  openSmaller: number | null
}

/**
 * The name a unit text will be saved as: the shop's own unit, else the new one. The words say
 * "1 Piece" and "per Piece", so the shop's "Pieces" is named in the singular here.
 */
export function twoUnitName(units: UnitRow[], text: string): string {
  const t = text.trim()
  if (!t) return ''
  const name = findUnit(units, t)?.name ?? unitForNew(t).name
  return presetFor(name)?.symbol === 'pcs' ? 'Piece' : name
}

const many = pluralUnit

/** One of the two units: the shop's own, a new one, or "+ New unit…" (board ProdUnitPick). */
function UnitPick({
  label,
  value,
  other,
  units,
  disabled,
  onPick,
}: {
  label: string
  value: string
  other: string
  units: UnitRow[]
  disabled: boolean
  onPick: (name: string) => void
}) {
  const [adding, setAdding] = useState(false)
  const [text, setText] = useState('')
  const row = value.trim() ? findUnit(units, value) : null
  const otherRow = other.trim() ? findUnit(units, other) : null
  const fresh = value.trim() && !row ? value.trim() : null
  const done = () => {
    if (text.trim()) onPick(text.trim())
    setAdding(false)
    setText('')
  }
  if (adding) {
    return (
      <span className="flex min-w-0 flex-1 gap-1.5">
        <Input
          aria-label={`New ${label.toLowerCase()}`}
          placeholder="Sachet"
          value={text}
          autoFocus
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              done()
            }
            if (e.key === 'Escape') {
              e.stopPropagation()
              setAdding(false)
            }
          }}
        />
        <Button variant="secondary" onClick={done}>
          Add
        </Button>
      </span>
    )
  }
  return (
    <span className="min-w-0 flex-1">
      <Select
        aria-label={label}
        disabled={disabled}
        value={row?.id ?? (fresh ? '__fresh' : '')}
        onChange={(e) => {
          const v = e.target.value
          if (v === '__new') return setAdding(true)
          const picked = units.find((u) => u.id === v)
          if (picked) onPick(picked.name)
        }}
      >
        <option value="" disabled>
          Choose…
        </option>
        {units.map((u) => (
          <option key={u.id} value={u.id} disabled={u.id === otherRow?.id}>
            {u.name}
          </option>
        ))}
        {fresh && <option value="__fresh">{unitForNew(fresh).name} (new)</option>}
        <option value="__new">+ New unit…</option>
      </Select>
    </span>
  )
}

/**
 * Sold in two units (docs/plans/two-units.md, boards ProdTwoUnits and ProdUnitSaved): both units
 * chosen from the shop's own, a price for each, and which one stock is counted in.
 */
export function TwoUnitsEditor({
  draft,
  onChange,
  units,
  quick,
  locked,
  isNew,
  stockIn,
  onStockIn,
  price,
  onPrice,
  checks,
  stockMilli,
  savedNames,
  currency,
}: {
  draft: TwoUnitsDraft
  onChange: (patch: Partial<TwoUnitsDraft>) => void
  units: UnitRow[]
  quick: QuickPair[]
  /** Already sold or received in these units: the count and the stock unit stay. */
  locked: boolean
  isNew: boolean
  stockIn: UnitChoice | null
  onStockIn: (choice: UnitChoice) => void
  /** The product's selling price: per the unit stock is counted in. */
  price: number | null
  onPrice: (paisa: number | null) => void
  checks: TwoUnitsChecks
  stockMilli: number
  /** The names a saved product had, to say which of them are not the shop's units yet. */
  savedNames: { larger: string; smaller: string } | null
  currency: string
}) {
  const larger = twoUnitName(units, draft.larger) || 'larger unit'
  const smaller = twoUnitName(units, draft.smaller) || 'smaller unit'
  const count = draft.count ?? 0
  const valid = count >= 2 && count <= 1000
  const u: TwoUnits = {
    larger_name: larger,
    smaller_name: smaller,
    count: valid ? count : 2,
    stock_in: stockIn ?? 'smaller',
  }
  const stockName = unitName(u, u.stock_in)
  const follows = draft.otherPricePaisa === null
  const fallback = stockIn !== null && price !== null && valid ? defaultOtherPrice(price, u) : null
  const otherPrice = draft.otherPricePaisa ?? fallback
  const priceOf = (side: UnitChoice) => (side === u.stock_in ? price : otherPrice)
  const blocking = (field: string) => checks.blocking.find((b) => b.field === field)?.text
  const warning = (field: string) => checks.warnings.find((w) => w.field === field)?.text
  const named = !!draft.larger.trim() && !!draft.smaller.trim()
  const unknown = savedNames
    ? [savedNames.larger, savedNames.smaller].filter((n) => n.trim() && !findUnit(units, n))
    : []
  const priceBox = (side: UnitChoice) => {
    const name = unitName(u, side)
    const mine = side === u.stock_in
    // A loose one dearer than its share is said under the smaller unit's price.
    const share = side === 'smaller' ? warning('other') : undefined
    const how = mine ? null : follows ? (
      <span className="inline-flex items-center gap-1">
        <Link2 className="size-3.5" aria-hidden /> From the {unitName(u, u.stock_in).toLowerCase()}{' '}
        price
      </span>
    ) : (
      <span>
        Own price ·{' '}
        <button
          type="button"
          className="font-medium text-primary underline-offset-2 hover:underline"
          onClick={() => onChange({ otherPricePaisa: null })}
        >
          Use the {unitName(u, u.stock_in).toLowerCase()} price again
        </button>
      </span>
    )
    return (
      <Field
        label={`Price per ${name}`}
        hint={
          stockIn === null ? (
            'Choose which unit stock is counted in first.'
          ) : share || how ? (
            <>
              {share && <span className="block text-warning">{share}</span>}
              {how}
            </>
          ) : undefined
        }
      >
        {(id) => (
          <MoneyInput
            id={id}
            prefix={currency}
            className="font-semibold"
            disabled={stockIn === null}
            placeholder="0.00"
            paisa={mine ? price : otherPrice}
            onValue={(p) => (mine ? onPrice(p) : onChange({ otherPricePaisa: p }))}
          />
        )}
      </Field>
    )
  }
  return (
    <div id="two-units" className="grid gap-3 sm:col-span-2 sm:grid-cols-2">
      <Switch
        className="sm:col-span-2"
        label="Also sold in a second unit"
        description="Like a strip and its tablets, or a carton and its packets."
        checked={draft.on}
        onChange={(e) => onChange({ on: e.target.checked })}
      />
      {draft.on && (
        <>
          <fieldset className="min-w-0 sm:col-span-2">
            <legend className="mb-1 text-sm font-medium text-muted">How they fit</legend>
            <div className="flex items-center gap-2">
              <span className="font-semibold">1</span>
              <UnitPick
                label="Larger unit"
                value={draft.larger}
                other={draft.smaller}
                units={units}
                disabled={locked}
                onPick={(name) => onChange({ larger: name })}
              />
              <span className="font-semibold">=</span>
              <span className="w-16 flex-none sm:w-20">
                <Input
                  aria-label={`How many ${many(smaller).toLowerCase()} in 1 ${larger.toLowerCase()}`}
                  inputMode="numeric"
                  placeholder="10"
                  className="text-right tabular"
                  disabled={locked}
                  value={draft.count ?? ''}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 4)
                    onChange({ count: digits ? Number(digits) : null })
                  }}
                />
              </span>
              <UnitPick
                label="Smaller unit"
                value={draft.smaller}
                other={draft.larger}
                units={units}
                disabled={locked}
                onPick={(name) => onChange({ smaller: name })}
              />
              {locked && <LockKeyhole className="size-4 flex-none text-subtle" aria-hidden />}
            </div>
            {locked ? (
              <p className="mt-1 text-xs text-subtle">
                Already used on a bill or delivery. A new pack size is a new product.
              </p>
            ) : blocking('count') && draft.count !== null ? (
              <p className="mt-1 text-xs text-danger">{blocking('count')}</p>
            ) : null}
            {named && blocking('units')?.startsWith('Choose two') && (
              <div className="mt-2 overflow-hidden rounded-xl">
                <Banner tone="danger">{blocking('units')}</Banner>
              </div>
            )}
          </fieldset>
          {!locked && quick.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
              <span className="text-xs text-subtle">Quick picks</span>
              {quick.map((q) => {
                const on =
                  twoUnitName(units, draft.larger) === twoUnitName(units, q.larger) &&
                  twoUnitName(units, draft.smaller) === twoUnitName(units, q.smaller) &&
                  draft.count === q.count
                return (
                  <button
                    key={q.label}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      onChange({ larger: q.larger, smaller: q.smaller, count: q.count })
                    }
                    className={cx(
                      'h-8 rounded-full border px-3 text-xs font-medium',
                      on
                        ? 'border-primary bg-primary-light text-primary'
                        : 'border-border bg-surface hover:border-primary/40',
                    )}
                  >
                    {q.label}
                  </button>
                )
              })}
            </div>
          )}
          {priceBox('larger')}
          {priceBox('smaller')}
          {locked && stockIn !== null ? (
            <p className="flex items-center gap-2 rounded-xl bg-soft px-3 py-2.5 text-sm text-muted sm:col-span-2">
              <LockKeyhole className="size-4 flex-none" aria-hidden />
              <span>
                <b className="text-ink">Stock counted in {many(stockName)}.</b> Cost, MRP and stock
                are per {stockName}.
              </span>
            </p>
          ) : (
            <div className="sm:col-span-2">
              <p id="stock-unit-question" className="mb-1.5 text-sm font-medium text-muted">
                Count stock in
              </p>
              <div
                role="radiogroup"
                aria-labelledby="stock-unit-question"
                className="grid grid-cols-2 gap-2.5"
              >
                {(['smaller', 'larger'] as const).map((choice) => {
                  const name = unitName(u, choice)
                  const on = stockIn === choice
                  return (
                    <button
                      key={choice}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => onStockIn(choice)}
                      className={cx(
                        'flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left',
                        on ? 'border-primary bg-primary-light' : 'border-border bg-surface',
                      )}
                    >
                      <span
                        aria-hidden
                        className={cx(
                          'mt-0.5 size-4 flex-none rounded-full bg-surface',
                          on ? 'border-[5px] border-primary' : 'border-[1.5px] border-subtle',
                        )}
                      />
                      <span>
                        <span className="block text-sm font-semibold">{name}</span>
                        <span className="block text-xs text-muted">
                          {choice === 'smaller'
                            ? `Loose ${many(name).toLowerCase()} can be sold and counted`
                            : `Only whole ${many(name).toLowerCase()} are counted`}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
              <p className="mt-1.5 text-xs text-muted">
                {stockIn === null
                  ? 'Choose one: your selling price and stock are per this unit.'
                  : `Cost, MRP and opening stock are per ${stockName}.`}
                {!isNew && stockMilli !== 0 && stockIn !== null && (
                  <>
                    {' '}
                    Your {qty(stockMilli)} in stock stay as {unitWords(stockMilli, stockName)}.
                  </>
                )}
              </p>
              {warning('stockIn') && (
                <div className="mt-2 overflow-hidden rounded-xl">
                  <Banner tone="warning">{warning('stockIn')}</Banner>
                </div>
              )}
            </div>
          )}
          {warning('mrp') && (
            <div className="overflow-hidden rounded-xl sm:col-span-2">
              <Banner tone="warning">{warning('mrp')}</Banner>
            </div>
          )}
          {named && valid && stockIn !== null && price !== null && (
            <div className="rounded-xl bg-soft px-3 py-2.5 text-sm text-muted sm:col-span-2">
              <p className="text-xs font-semibold text-ink">On a bill</p>
              <p>
                1 {larger} = {money(priceOf('larger') ?? 0)} · 1 {smaller} ={' '}
                {money(priceOf('smaller') ?? 0)}
                {!isNew && stockMilli !== 0 && <> · in stock {stockWords(stockMilli, u)}</>}
              </p>
            </div>
          )}
          {stockIn === 'larger' && valid && 1000 % count !== 0 && (
            <p className="text-sm text-muted sm:col-span-2">
              With {count} in a {larger.toLowerCase()}, stock may be off by a tiny part of a{' '}
              {smaller.toLowerCase()}. A stock count corrects it.
            </p>
          )}
          {unknown.length > 0 && (
            <div className="overflow-hidden rounded-xl sm:col-span-2">
              <Banner tone="info">
                {unknown.map((n) => `“${n}”`).join(' and ')}{' '}
                {unknown.length > 1 ? 'aren’t' : 'isn’t'} one of your units yet: saving adds{' '}
                {unknown.length > 1 ? 'them' : 'it'} to your units. Or choose one of yours above.
              </Banner>
            </div>
          )}
        </>
      )}
    </div>
  )
}
